'use client';

import type { ReactNode } from 'react';
import { HarEntriesRenderer } from '../../ToolCallsRenderers/renderers/HarEntriesRenderer';
import { JSONRenderer } from '../../ToolCallsRenderers/renderers/JSONRenderer';
import { PlainTextRenderer } from '../../ToolCallsRenderers/renderers/PlainTextRenderer';
import {
  parseHarEntriesFromObject,
  parseHarEntriesFromMarkdown,
} from '../../content-utils/content-parsers';
import type { OutputRenderContext } from '../../tool-config-types';

export const renderHarEntriesResult = (ctx: OutputRenderContext): ReactNode => {
  const { content, maxHeight } = ctx;

  let harEntriesData: ReturnType<typeof parseHarEntriesFromObject> | null =
    null;
  let rawContent: string | undefined;

  // Try to parse from object first (structured output from tool)
  if (typeof content === 'object' && content !== null) {
    harEntriesData = parseHarEntriesFromObject(content);
    rawContent = JSON.stringify(content, null, 2);
  } else if (typeof content === 'string') {
    rawContent = content;
    // Try to parse as JSON first (new structured format)
    try {
      const parsed = JSON.parse(content);
      harEntriesData = parseHarEntriesFromObject(parsed);
    } catch {
      // If JSON parsing fails, try markdown format (legacy fallback)
      harEntriesData = parseHarEntriesFromMarkdown(content);
    }
  }

  // If we successfully parsed HAR entries data (even if empty), use the renderer
  if (harEntriesData) {
    return (
      <HarEntriesRenderer
        entries={harEntriesData.entries}
        query={harEntriesData.query}
        totalEntries={harEntriesData.totalEntries}
        rawContent={rawContent}
        maxHeight={maxHeight}
      />
    );
  }

  // Fallback for object content (only when parsing failed)
  if (typeof content === 'object') {
    return <JSONRenderer data={content} maxHeight={maxHeight} />;
  }

  // Fallback for string content
  return <PlainTextRenderer content={String(content)} maxHeight={maxHeight} />;
};
